import { APIError, defaultParseError, useSubmit } from "@certn/api";
import type { SubmitData } from "@superdoc-dev/esign";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { toSnakeCaseSubmitData } from "./helpers";

const INVALID_SIGNATURE_IMAGE_ERROR = "Invalid signature image";

export const INVALID_SIGNATURE_IMAGE_TOAST =
  "We couldn't save your signature. Please sign again.";

export const INVALID_SIGNATURE_IMAGE_RELOGIN_TOAST =
  "We couldn't save your signature. Please log out and log back in, then try again.";

/** True when the sign API rejected the PNG; backend string is for logs/ops. */
export function isInvalidSignatureImageError(error: APIError): boolean {
  return defaultParseError(error) === INVALID_SIGNATURE_IMAGE_ERROR;
}

interface UseESignSubmitOptions {
  onSuccess?: () => void | Promise<void>;
  onFailure?: (error: unknown) => void;
  /**
   * When true, rethrows on submit or post-submit `onSuccess` failure so
   * ESignDocument stays interactive for retry. SuperDoc consent/sign
   * callers should pass true so a 400 does not lock the accept checkbox.
   */
  throwErrors?: boolean;
}

/**
 * Hook for submitting e-sign signature data to the agreement-signature endpoint.
 * Accepts SuperDoc's native SubmitData (with signer fields already enriched
 * with `type` by ESignDocument), converts to Django snake_case,
 * and POSTs to the signing API with the template_id.
 *
 * Provides `isSubmitting` state and toast-on-error automatically.
 */
export function useESignSubmit(
  caseId: string,
  options?: UseESignSubmitOptions
) {
  const { t } = useTranslation();
  const throwErrors = options?.throwErrors ?? false;
  // Session-scoped: remount (reload / logout+login) resets the retry count.
  const invalidSignatureFailCountRef = useRef(0);

  const parseError = useCallback(
    (error: APIError) => {
      if (!isInvalidSignatureImageError(error)) {
        return undefined;
      }

      invalidSignatureFailCountRef.current += 1;
      // First 400: redraw in place. Second and later: ask them to remount via logout.
      if (invalidSignatureFailCountRef.current >= 2) {
        return t(
          "We couldn't save your signature. Please log out and log back in, then try again."
        );
      }
      return t("We couldn't save your signature. Please sign again.");
    },
    [t]
  );

  const { submit, isSubmitting, error } = useSubmit<unknown>(
    `/api/internal/applicant/case/${caseId}/agreement-signature/sign/`,
    {
      onFailure: options?.onFailure,
      parseError,
      showToastOnError: true,
      throwErrors,
      // When throwErrors is false, run onSuccess inside useSubmit so failed POSTs
      // do not invoke the callback. When true, run onSuccess in submitSignature
      // instead — useSubmit swallows onSuccess errors if onFailure is set.
      onSuccess:
        !throwErrors && options?.onSuccess
          ? async () => {
              await options.onSuccess?.();
            }
          : undefined,
    }
  );

  const submitSignature = useCallback(
    async (data: SubmitData, templateId: string) => {
      const snakeCaseData = toSnakeCaseSubmitData(data);
      await submit({ ...snakeCaseData, template_id: templateId });

      if (!throwErrors || !options?.onSuccess) {
        return;
      }

      try {
        await options.onSuccess();
      } catch (e) {
        options?.onFailure?.(e);
        throw e;
      }
    },
    [options, submit, throwErrors]
  );

  return { submitSignature, isSubmitting, error };
}
